import { Box, Typography, Grid, Link, Divider } from '@mui/material'
import Decimal from 'decimal.js'
import { ItemType, UnavailableItemsContract, VoidCompReasonType } from 'models'
import { values, max } from 'lodash-es'
import { useCallback, useMemo, useState } from 'react'
import { useFormat } from 'Services/localize/format'
import {
    DiscountContractWithItemId,
    OrderItemViewModel,
    SelectItemInCartType,
} from 'store/models/sellModels'
import { getItemPrice } from 'utilities/price/getPrice'
import { CartComp } from './CartComp'
import CartDiscount from './CartDiscount'
import CartItemDetail from './CartItemDetail'
import { CartVoid } from './CartVoid'
import { getVariationItem } from '../../../../components/Sell/utils/common'
import ItemCart from 'views/Sale/SaleUI/Cart/ItemCartUI'
import { useCartContext } from './CartContext'
import { isOpenItemId } from 'constant/magicNumber'
import { useScreenSize } from 'hooks/useScreenSize/useScreenSize'
import CallSplitIcon from '@mui/icons-material/CallSplit'
import { isVoidedItem } from 'views/Sale/utils/orderItemUtil'
export interface ICartItemProps {
    item: OrderItemViewModel
    isReadonly?: boolean
    onSelectItem?: (item: OrderItemViewModel) => void
    onDoubleClick?: (item: OrderItemViewModel) => void
    onRemoveItem?: (item: OrderItemViewModel) => void
    onRemoveComp?: (item: OrderItemViewModel) => void
    onRemoveDiscount?: (discount: DiscountContractWithItemId) => void
    displayAssignTo?: boolean
    highlightNewItem?: boolean
    onClickAssignTo?: (item: OrderItemViewModel) => void
    isSplitedLine?: boolean
    onValidateCartItems?: (unavailableProduct: UnavailableItemsContract) => void
    disableEditOrder?: boolean
}
export function CartItem(props: ICartItemProps) {
    const {
        item,
        isReadonly = false,
        onSelectItem,
        onDoubleClick,
        onRemoveItem,
        onRemoveComp,
        onRemoveDiscount,
        displayAssignTo,
        highlightNewItem,
        onClickAssignTo,
        isSplitedLine,
        disableEditOrder = false,
    } = props

    const { isMobile } = useScreenSize()
    const { currency } = useFormat()
    const {
        priceListData,
        selectedItemInCart,
        onSelectDiscountOrComp,
    } = useCartContext()
    const errorMessage = useMemo(() => item.error, [item.error])
    const assignCount = useMemo(() => {
        return max([item.assignTo?.length ?? 0, 1])!
    }, [item.assignTo?.length])
    const selectedItem = useMemo(
        () =>
            selectedItemInCart?.type === SelectItemInCartType.item
                ? selectedItemInCart.data
                : undefined,
        [selectedItemInCart],
    )

    const setSelectDiscount = useCallback(
        (discount: DiscountContractWithItemId) => {
            onSelectDiscountOrComp?.({
                type: SelectItemInCartType.discount,
                data: discount,
            })
        },
        [onSelectDiscountOrComp],
    )

    const isVoidItem = useMemo(() => isVoidedItem(item), [item])

    const isSelected = useMemo(() => selectedItem && item === selectedItem, [
        item,
        selectedItem,
    ])

    const rawPrice = useMemo(() => {
        let itemID = item.item.itemId
        if (item.item.type === ItemType.BaseProduct) {
            const usedVariants = values(item.usedVariants).sort()
            const itemVariant = getVariationItem(item.item, usedVariants)
            itemID = itemVariant?.itemId ?? ''
        }
        const totalDue = new Decimal(
            getItemPrice(itemID, item.menuId, priceListData),
        ).mul(item.amount)
        return isSplitedLine ? totalDue.div(assignCount) : totalDue
    }, [
        assignCount,
        isSplitedLine,
        item.amount,
        item.item,
        item.menuId,
        item.usedVariants,
        priceListData,
    ])

    const subTotalPrice = useMemo(() => {
        const subTotal = new Decimal(item.lineTotal).mul(item.amount)
        return isSplitedLine ? subTotal.div(assignCount) : subTotal
    }, [assignCount, isSplitedLine, item])

    const subTotalAfterDiscount = useMemo(() => {
        const lineWithDiscount = subTotalPrice
            .sub(item.itemDiscountAmount ?? 0)
            .sub(item.totalCompDiscount ?? 0)

        return isSplitedLine
            ? lineWithDiscount.div(assignCount)
            : lineWithDiscount
    }, [
        assignCount,
        isSplitedLine,
        item.itemDiscountAmount,
        item.totalCompDiscount,
        subTotalPrice,
    ])
    /** has addistional price aka combo, modifier item and has more than 1 quantiy */
    const hasAddPrice = useMemo(
        () =>
            // open iten has no item.item
            !isOpenItemId(item.item.itemId) && !subTotalPrice.equals(rawPrice),
        [item, subTotalPrice, rawPrice],
    )

    /** Note: hide combo item error. */
    const itemWarning = useCallback(() => {
        return (
            <>
                {values(errorMessage)
                    .flatMap((i) => i)
                    .filter((i) => !i.isHiddenShowError)
                    .map((e, item) => (
                        <Box key={`${e}-${item}`}>
                            <Typography style={{ fontSize: 12 }} color="error">
                                {e.message}
                            </Typography>
                        </Box>
                    ))}
            </>
        )
    }, [errorMessage])

    const [openAssignDetail, setOpenAssignDetail] = useState<boolean>(false)

    const handleClick = () => {
        if (onSelectItem) {
            onSelectItem(item)
            setOpenAssignDetail((prev) => !prev)
        }
    }

    function SubTotal() {
        if (
            isMobile &&
            item.voidComp &&
            item.voidComp.type === VoidCompReasonType.Void
        )
            return <>{currency(subTotalPrice.toNumber())}</>

        return (
            <Box component={'span'}>
                {!subTotalPrice.equals(subTotalAfterDiscount) && (
                    <Typography
                        component={'span'}
                        sx={{
                            textDecoration: 'line-through',
                            mx: '5px',
                        }}
                    >
                        {currency(subTotalPrice.toNumber())}
                    </Typography>
                )}
                {currency(subTotalAfterDiscount.toNumber())}
            </Box>
        )
    }

    return (
        <ItemCart
            isReadonly={isReadonly}
            highlightNewItem={highlightNewItem}
            key={`${item.id}cart-item-detail`}
            onClick={handleClick}
            onDoubleClick={() => !isVoidItem && onDoubleClick?.(item)}
            item={item}
        >
            <ItemCart.Row
                isSelected={isReadonly ? false : isSelected!}
                isDisabled={isReadonly ? false : isVoidItem!}
            >
                <ItemCart.CartDetail
                    item={item}
                    openAssignDetail={openAssignDetail}
                    showTableAssignment={displayAssignTo ?? false}
                    onRemoveItem={onRemoveItem}
                    isVoidItem={isVoidItem}
                    disableEditOrder={disableEditOrder}
                    isReadonly={isReadonly}
                >
                    {(item) => (
                        <Grid
                            container
                            justifyContent={'flex-end'}
                            spacing={0.5}
                        >
                            {!isVoidItem && (
                                <Grid
                                    item
                                    xs="auto"
                                    color={
                                        item.item.type ===
                                        ItemType.DepositProduct
                                            ? 'primary.main'
                                            : item.item.type ===
                                              ItemType.CreditProduct
                                            ? 'error.main'
                                            : undefined
                                    }
                                >
                                    {hasAddPrice ? (
                                        // In case of Having Add Price will display RawPrice first, and display item total after
                                        currency(rawPrice.toNumber())
                                    ) : (
                                        <SubTotal />
                                    )}
                                </Grid>
                            )}
                        </Grid>
                    )}
                </ItemCart.CartDetail>
                <Grid item container direction="row">
                    <Grid item xs={2} lg={1} />
                    <Grid
                        item
                        xs={10}
                        lg={11}
                        color={(theme) =>
                            isSelected
                                ? theme.palette.default.contrastText
                                : theme.palette.bluegrey.main
                        }
                        fontSize={14}
                    >
                        <CartItemDetail isReadonly={isReadonly} item={item} />
                        {hasAddPrice && !isVoidItem && (
                            <Typography
                                textAlign={'right'}
                                whiteSpace={'nowrap'}
                            >
                                Item Total: <SubTotal />
                            </Typography>
                        )}
                        {item.note}
                        {!isReadonly && !isVoidItem && itemWarning()}
                        <CartVoid void={item.voidComp} />
                    </Grid>
                </Grid>
            </ItemCart.Row>
            {(item.discounts?.length ?? 0) > 0 && (
                <CartDiscount
                    totalLinePrice={subTotalPrice.toNumber()}
                    discounts={item.discounts ?? []}
                    id={item.id}
                    itemAmount={item.amount}
                    selectDiscount={
                        selectedItemInCart?.type ===
                        SelectItemInCartType.discount
                            ? selectedItemInCart.data
                            : undefined
                    }
                    margin={isMobile ? 6 : 3}
                    noPaddingRight={isMobile}
                    setSelectDiscount={setSelectDiscount}
                    onRemoveDiscount={onRemoveDiscount}
                    disableEditOrder={disableEditOrder}
                />
            )}
            <CartComp
                voidComp={item.voidComp!!}
                orderItem={item}
                setSelectedItemCart={onSelectDiscountOrComp}
                selectComp={
                    selectedItemInCart?.type === SelectItemInCartType.Comp
                        ? selectedItemInCart?.data
                        : undefined
                }
                margin={isMobile ? 6 : 3}
                noPaddingRight={isMobile}
                onRemoveComp={onRemoveComp}
            />
            {onClickAssignTo && (
                <Grid item xs={12} textAlign={'end'}>
                    <Link
                        underline="none"
                        color={(t) =>
                            disableEditOrder
                                ? t.palette.disabled.main
                                : t.palette.primary.main
                        }
                        onClick={(e) => {
                            if (disableEditOrder) return
                            e.stopPropagation()
                            onClickAssignTo?.(item)
                        }}
                    >
                        {(item.assignTo?.length ?? 0) > 1 ? (
                            <CallSplitIcon fontSize="small" color="primary" />
                        ) : (
                            'Assign to'
                        )}
                    </Link>
                    <Divider />
                </Grid>
            )}
        </ItemCart>
    )
}
